import { useMutation } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { messageForError } from '../api/errors'
import { requestPasswordReset } from '../api/password-resets'
import { takeEmailForReset } from '../auth/forgot-email'
import { AuthCard } from './ui/AuthCard'
import { Button } from './ui/Button'
import { FormError } from './ui/FormError'
import { TextField } from './ui/TextField'
import { TextLink } from './ui/TextLink'
import { useRedirectIfSignedIn } from './ui/useRedirectIfSignedIn'

export interface ForgotPasswordFormProps {
  /**
   * The parent is choosing a first password for an account provisioned at
   * checkout, not replacing a forgotten one: the copy says so, and the
   * platform is told so it can word the email to match. `data-create-password`.
   */
  createPassword?: boolean
  /** The sign-in page, for the way back. `data-login-url`. */
  loginUrl?: string
  /**
   * The parent's account page: where a signed-in visitor is sent, and where a
   * child's username and a new password come from. `data-account-url`.
   */
  accountUrl?: string
  /** A hosted logo to show above the heading; omit for none. `data-logo-url`. */
  logoUrl?: string
  /** Overrides the heading the page's purpose implies. */
  heading?: string
}

interface Copy {
  heading: string
  intro: string
  submit: string
  /** What is on its way, once the address has been posted. */
  sent: string
}

const FORGOT: Copy = {
  heading: 'Forgot your password?',
  intro: 'Enter the parent email on the account and we’ll send a link to choose a new password.',
  submit: 'Send reset link',
  sent: 'a link to choose a new password is on its way',
}

const CREATE: Copy = {
  heading: 'Create your password',
  intro:
    'Enter the parent email you used at checkout and we’ll send a link to create your password.',
  submit: 'Send link',
  sent: 'a link to create your password is on its way',
}

/**
 * Asks for the email a reset link should go to — for a forgotten password,
 * or for the first password on an account provisioned at checkout, which is
 * the same link with different words around it.
 *
 * The API sends the email only when the address has an account but answers
 * the same 201 either way, and this card says the same thing either way — so
 * neither the network nor the screen can confirm which addresses have
 * Codeverse accounts. The link is only ever emailed, never shown here.
 *
 * Kids have no email. A child who has forgotten a username or a password is
 * helped by a parent on the account page, so the card says so rather than let
 * them type a username and wait for an email that never comes.
 */
export function ForgotPasswordForm({
  createPassword = false,
  loginUrl = '/login',
  accountUrl = '/account',
  logoUrl,
  heading,
}: ForgotPasswordFormProps) {
  const copy = createPassword ? CREATE : FORGOT

  // Someone who arrives already signed in has a password; the account page is
  // where they change it.
  useRedirectIfSignedIn(accountUrl)

  // Prefilled by the link that brought them, or by the login form after a
  // failed sign-in.
  const [email, setEmail] = useState(() => takeEmailForReset(window.location.search))
  const [error, setError] = useState<string | null>(null)

  const request = useMutation({
    mutationFn: () => requestPasswordReset({ email, createPassword }),
    onError: (cause) => {
      setError(messageForError(cause))
      // Every failure here is transport or ours; the parent gets a generic message.
      console.error('[codeverse] password reset request failed', cause)
    },
  })

  const canSubmit = email.trim() !== '' && !request.isPending

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit) return
    setError(null)
    request.mutate()
  }

  if (request.isSuccess) {
    return (
      <AuthCard heading="Check your email" logoUrl={logoUrl}>
        <p className="mt-6 text-center text-ink-soft" data-testid="reset-sent">
          If <span className="font-medium text-ink">{email.trim()}</span> has a Codeverse account,{' '}
          {copy.sent}. It works for an hour.
        </p>
        <p className="mt-5 text-center">
          <TextLink href={loginUrl}>Back to sign in</TextLink>
        </p>
      </AuthCard>
    )
  }

  return (
    <AuthCard heading={heading ?? copy.heading} logoUrl={logoUrl}>
      <form onSubmit={onSubmit} noValidate>
        <p className="mt-4 text-center text-sm text-ink-soft">{copy.intro}</p>

        {error && <FormError data-testid="forgot-error">{error}</FormError>}

        <div className="mt-6">
          <TextField
            label="Email"
            placeholder="Email"
            name="email"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={request.isPending}
            data-testid="email"
          />
        </div>

        <div className="mt-6">
          <Button type="submit" block disabled={!canSubmit} data-testid="submit">
            {request.isPending ? 'Sending…' : copy.submit}
          </Button>
        </div>

        {!createPassword && (
          <p className="mt-5 text-center text-sm text-ink-soft" data-testid="kids-note">
            Forgot a child’s username or password? A parent can find the username and set a new
            password on the <TextLink href={accountUrl}>account page</TextLink>.
          </p>
        )}

        <p className={createPassword ? 'mt-5 text-center' : 'mt-3 text-center'}>
          <TextLink href={loginUrl}>Back to sign in</TextLink>
        </p>
      </form>
    </AuthCard>
  )
}
